import {
  GetExtranetShopProductsQueryDto,
  MultilingualKeyDto,
  OrderedShopProductsQuerySortKeys,
} from '@pharmac-extranet/extranet-api'
import {
  ControlledDateRangePicker,
  ControlledMultiSelect,
  ControlledSelect,
  useBreakpoint,
  useLanguage,
} from '@pharmac-extranet/shared'
import { PharmacButton, PharmacLabel, PharmacSelect } from '@pharmac/ui'
import { useQuery } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { Control, FieldValues, FormProvider, useForm } from 'react-hook-form'
import { createSearchParams, useSearchParams } from 'react-router-dom'
import { cleanQueryParams } from '../../features/infinite-query/util/cleanQueryParams'
import { useExtranetPath } from '../../router/useExtranetPath'
import { api } from '../../services/HttpService'
import {
  ExtranetFiltersColumn,
  ExtranetFiltersFooter,
  ExtranetFiltersRow,
} from '../filters/ExtranetFiltersComponents'
import { mapShopProductSearchParamsToQueryParams } from './util/query-parameter-mapping'

type Props = {
  closeFilters: () => void
}

export const ShopProductFilters = ({ closeFilters }: Props) => {
  const { t, key, i18n } = useLanguage()
  const { maxMobile } = useBreakpoint()
  const [searchParams, setSearchParams] = useSearchParams()
  const [filters, setFilters] = useState(mapShopProductSearchParamsToQueryParams(searchParams))
  const createPath = useExtranetPath()
  const currentUrl = window.location.pathname
  const allProductsTab = currentUrl === createPath('shop')
  const ownProductTab = currentUrl === createPath('shop.own')
  const { minDesktop } = useBreakpoint()

  const sortBy: Array<{
    id: number
    sortOrder: 'asc' | 'desc' | 'undefined'
    sortKey: OrderedShopProductsQuerySortKeys | 'undefined'
    name: MultilingualKeyDto
  }> = [
    {
      id: 0,
      sortOrder: 'undefined',
      sortKey: 'undefined',
      name: {
        fi: '-',
        en: '-',
        sv: '-',
      },
    },
    {
      id: 1,
      sortOrder: 'asc',
      sortKey: OrderedShopProductsQuerySortKeys.Title,
      name: {
        fi: t('extranet:shop.filters.alphabetically'),
        en: t('extranet:shop.filters.alphabetically'),
        sv: t('extranet:shop.filters.alphabetically'),
      },
    },
    {
      id: 2,
      sortOrder: 'desc',
      sortKey: OrderedShopProductsQuerySortKeys.PublishDate,
      name: {
        fi: t('extranet:shop.filters.newest'),
        en: t('extranet:shop.filters.newest'),
        sv: t('extranet:shop.filters.newest'),
      },
    },
    {
      id: 3,
      sortOrder: 'asc',
      sortKey: OrderedShopProductsQuerySortKeys.PublishDate,
      name: {
        fi: t('extranet:shop.filters.oldest'),
        en: t('extranet:shop.filters.oldest'),
        sv: t('extranet:shop.filters.oldest'),
      },
    },
    {
      id: 4,
      sortOrder: 'asc',
      sortKey: OrderedShopProductsQuerySortKeys.UnitSellingPrice,
      name: {
        fi: t('extranet:shop.filters.cheapest'),
        en: t('extranet:shop.filters.cheapest'),
        sv: t('extranet:shop.filters.cheapest'),
      },
    },
    {
      id: 5,
      sortOrder: 'desc',
      sortKey: OrderedShopProductsQuerySortKeys.UnitSellingPrice,
      name: {
        fi: t('extranet:shop.filters.mostExpensive'),
        en: t('extranet:shop.filters.mostExpensive'),
        sv: t('extranet:shop.filters.mostExpensive'),
      },
    },
  ]

  const { data: materialProductTags } = useQuery({
    queryKey: ['__materialProductTags', i18n.language],
    queryFn: async () =>
      await api.materialProducts.getMaterialProductTags({
        lang: i18n.language,
      }),
  })

  const formhook = useForm<GetExtranetShopProductsQueryDto>({
    mode: 'onChange',
    defaultValues: {
      tagIds: filters.tagIds ?? [],
      freeOrPaidProduct: filters.freeOrPaidProduct ?? undefined,
      productFamily: filters.productFamily ?? undefined,
      productGroupId: filters.productGroupId ?? undefined,
      filterByLanguage: filters.filterByLanguage ?? undefined,
      sortKey: filters.sortKey ?? undefined,
      sortOrder: filters.sortOrder ?? undefined,
      publishDateRangeStart: filters.publishDateRangeStart ?? '',
      publishDateRangeEnd: filters.publishDateRangeEnd ?? '',
    },
  })

  const selectedSortKey = formhook.watch('sortKey')
  const selectedSortOrder = formhook.watch('sortOrder')

  useEffect(() => {
    const params = mapShopProductSearchParamsToQueryParams(searchParams)

    setFilters(params)

    formhook.reset({
      tagIds: params.tagIds ?? [],
      freeOrPaidProduct: params.freeOrPaidProduct ?? undefined,
      productFamily: params.productFamily ?? undefined,
      productGroupId: params.productGroupId ?? undefined,
      filterByLanguage: params.filterByLanguage ?? undefined,
      sortKey: params.sortKey ?? undefined,
      sortOrder: params.sortOrder ?? undefined,
      publishDateRangeStart: params.publishDateRangeStart ?? '',
      publishDateRangeEnd: params.publishDateRangeEnd ?? '',
    })
  }, [searchParams])

  const handleSubmit = (values: GetExtranetShopProductsQueryDto) => {
    setSearchParams(
      createSearchParams(
        cleanQueryParams({
          ...filters,
          ...values,
        })
      )
    )
    closeFilters()
  }

  const sortOnChange = (value: string) => {
    const splittedValue = value.split('/')
    const key = splittedValue[0]
    const order = splittedValue[1]
    if (key === 'undefined') {
      formhook.setValue('sortKey', undefined, { shouldDirty: true })
      formhook.setValue('sortOrder', undefined, { shouldDirty: true })
      return
    }
    formhook.setValue('sortKey', key as OrderedShopProductsQuerySortKeys, { shouldDirty: true })
    formhook.setValue('sortOrder', order as 'asc' | 'desc', { shouldDirty: true })
  }

  return (
    <FormProvider {...formhook}>
      <div
        style={{
          minHeight: allProductsTab ? 'max-content' : '200px',
          marginBottom: maxMobile ? (ownProductTab ? '200px' : '100px') : 0,
        }}
      >
        <ExtranetFiltersRow
          style={{
            display: 'flex',
            justifyContent: 'space-around',
          }}
        >
          <ExtranetFiltersColumn>
            <div style={{ width: '100%' }}>
              <PharmacLabel marginSmallBottom>{t('extranet:shop.filters.order')}</PharmacLabel>
              <PharmacSelect
                name="sortKey"
                value={`${selectedSortKey}/${selectedSortOrder}`}
                onChange={(value) => sortOnChange(value)}
              >
                {sortBy?.map((a) => (
                  <option
                    key={a.id}
                    value={`${a.sortKey}/${a.sortOrder}`}
                  >
                    {key(a.name)}
                  </option>
                ))}
              </PharmacSelect>
            </div>
          </ExtranetFiltersColumn>
          {allProductsTab && (
            <ExtranetFiltersColumn>
              <div style={{ width: '100%' }}>
                <ControlledSelect
                  control={formhook.control as unknown as Control<FieldValues>}
                  label={t('extranet:shop.filters.priceType')}
                  name="freeOrPaidProduct"
                  falsyAsUndefined
                >
                  <option
                    key={0}
                    value="undefined"
                  >
                    -
                  </option>
                  <option
                    key={1}
                    value="free"
                  >
                    {t('extranet:shop.filters.free')}
                  </option>
                  <option
                    key={2}
                    value="paid"
                  >
                    {t('extranet:shop.filters.fee')}
                  </option>
                </ControlledSelect>
              </div>
            </ExtranetFiltersColumn>
          )}
          {allProductsTab && (
            <ExtranetFiltersColumn>
              <ControlledDateRangePicker
                control={formhook.control as unknown as Control<FieldValues>}
                label={t('extranet:shopProducts.releaseDateRange')}
                startDateName="publishDateRangeStart"
                endDateName="publishDateRangeEnd"
                startDateText={t('startDateText', { ns: 'common' })}
                endDateText={t('endDateText', { ns: 'common' })}
                placeholder={t('selectDateRange', { ns: 'common' })}
                weekText={t('week', { ns: 'common' })}
                width={minDesktop ? '255px' : '275px'}
                calendarPlacement="bottom"
              />
            </ExtranetFiltersColumn>
          )}
          {ownProductTab && (
            <>
              <ExtranetFiltersColumn>
                <div style={{ width: '100%' }}>
                  <ControlledSelect
                    control={formhook.control as unknown as Control<FieldValues>}
                    label={t('common:language')}
                    name="filterByLanguage"
                    falsyAsUndefined
                  >
                    <option
                      key={0}
                      value="undefined"
                    >
                      -
                    </option>
                    <option
                      key={1}
                      value="fi"
                    >
                      {t('common:fi')}
                    </option>
                    <option
                      key={2}
                      value="sv"
                    >
                      {t('common:sv')}
                    </option>
                    <option
                      key={3}
                      value="en"
                    >
                      {t('common:en')}
                    </option>
                  </ControlledSelect>
                </div>
              </ExtranetFiltersColumn>
              <ExtranetFiltersColumn>
                <ControlledDateRangePicker
                  control={formhook.control as unknown as Control<FieldValues>}
                  label={t('extranet:shopProducts.releaseDateRange')}
                  startDateName="publishDateRangeStart"
                  endDateName="publishDateRangeEnd"
                  startDateText={t('startDateText', { ns: 'common' })}
                  endDateText={t('endDateText', { ns: 'common' })}
                  placeholder={t('selectDateRange', { ns: 'common' })}
                  weekText={t('week', { ns: 'common' })}
                  calendarPlacement="bottom"
                  width={minDesktop ? '255px' : '275px'}
                />
              </ExtranetFiltersColumn>
              <ExtranetFiltersColumn>
                <div style={{ width: '100%' }}>
                  <ControlledMultiSelect
                    control={formhook.control as unknown as Control<FieldValues>}
                    label={t('extranet:shop.filters.tags')}
                    name="tagIds"
                  >
                    {materialProductTags?.map((a) => (
                      <option
                        key={a.id}
                        value={a.id}
                      >
                        {key(a.name)}
                      </option>
                    ))}
                  </ControlledMultiSelect>
                </div>
              </ExtranetFiltersColumn>
            </>
          )}
        </ExtranetFiltersRow>
        {allProductsTab && (
          <ExtranetFiltersRow
            style={{
              display: 'flex',
              justifyContent: 'space-around',
            }}
          >
            <ExtranetFiltersColumn>
              <div style={{ width: '100%' }}>
                <ControlledSelect
                  control={formhook.control as unknown as Control<FieldValues>}
                  label={t('common:language')}
                  name="filterByLanguage"
                  falsyAsUndefined
                >
                  <option
                    key={0}
                    value="undefined"
                  >
                    -
                  </option>
                  <option
                    key={1}
                    value="fi"
                  >
                    {t('common:fi')}
                  </option>
                  <option
                    key={2}
                    value="sv"
                  >
                    {t('common:sv')}
                  </option>
                  <option
                    key={3}
                    value="en"
                  >
                    {t('common:en')}
                  </option>
                </ControlledSelect>
              </div>
            </ExtranetFiltersColumn>
            <ExtranetFiltersColumn>
              <div style={{ width: '100%' }}>
                <ControlledMultiSelect
                  control={formhook.control as unknown as Control<FieldValues>}
                  label={t('extranet:shop.filters.tags')}
                  name="tagIds"
                >
                  {materialProductTags?.map((a) => (
                    <option
                      key={a.id}
                      value={a.id}
                    >
                      {key(a.name)}
                    </option>
                  ))}
                </ControlledMultiSelect>
              </div>
            </ExtranetFiltersColumn>
            <ExtranetFiltersColumn />
          </ExtranetFiltersRow>
        )}
      </div>
      <ExtranetFiltersFooter>
        <PharmacButton
          onClick={formhook.handleSubmit(handleSubmit)}
          disabled={!formhook.formState.isDirty}
        >
          {t('search', { ns: 'common' })}
        </PharmacButton>
      </ExtranetFiltersFooter>
    </FormProvider>
  )
}
